import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  ChevronLeft, ChevronRight, Plus, X, Calendar, Droplet, AlertTriangle, 
  MapPin, Ship, Lock, Trash2, Edit2, Check, Clock, User, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configurazione tipi evento
const EVENT_TYPES = {
  collection: { label: 'Raccolta', icon: Droplet, color: '#06b6d4', bgColor: 'bg-cyan-500/20', textColor: 'text-cyan-400' },
  sos: { label: 'SOS', icon: AlertTriangle, color: '#ef4444', bgColor: 'bg-red-500/20', textColor: 'text-red-400' },
  waste_report: { label: 'Segnalazione', icon: MapPin, color: '#eab308', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400' },
  event: { label: 'Evento', icon: Calendar, color: '#22c55e', bgColor: 'bg-emerald-500/20', textColor: 'text-emerald-400' },
  underwater: { label: 'Sub', icon: Ship, color: '#0ea5e9', bgColor: 'bg-sky-500/20', textColor: 'text-sky-400' },
  private: { label: 'Privato', icon: Lock, color: '#6b7280', bgColor: 'bg-slate-500/20', textColor: 'text-slate-400' }
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const CalendarSection = () => {
  const { token, user, isAdmin } = useAuth();
  const { language } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteForm, setNoteForm] = useState({ title: '', description: '', date: '', time: '12:00' });
  const [editingNote, setEditingNote] = useState(null);
  const [filters, setFilters] = useState({
    collection: true, sos: true, waste_report: true, event: true, underwater: true, private: true
  });
  const [showFilters, setShowFilters] = useState(false);

  // Formatta data per l'API
  const getMonthString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Fetch eventi
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/events?month=${getMonthString(currentDate)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      toast.error('Errore caricamento calendario');
    } finally {
      setLoading(false);
    }
  }, [token, currentDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Calcola i giorni del mese
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Giorno della settimana del primo giorno (0 = Domenica, ma vogliamo Lunedì = 0)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    // Giorni vuoti prima
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Giorni del mese
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  // Ottieni eventi per un giorno specifico
  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      const eventDate = e.date.split('T')[0];
      return eventDate === dateStr && filters[e.type];
    });
  };

  // Navigazione mesi
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(new Date().getDate());
  };

  // Gestione note private
  const handleAddNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Inserisci un titolo');
      return;
    }
    if (!noteForm.date) {
      toast.error('Seleziona una data');
      return;
    }

    const dateTime = `${noteForm.date}T${noteForm.time}:00`;
    
    try {
      if (editingNote) {
        await axios.put(
          `${BACKEND_URL}/api/calendar/private-note/${editingNote}`,
          { title: noteForm.title, description: noteForm.description, date: dateTime },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Nota aggiornata');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/calendar/private-note`,
          { title: noteForm.title, description: noteForm.description, date: dateTime },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Nota aggiunta');
      }
      
      setShowAddNote(false);
      setEditingNote(null);
      setNoteForm({ title: '', description: '', date: '', time: '12:00' });
      fetchEvents();
    } catch (error) {
      toast.error('Errore salvataggio nota');
    }
  };

  const handleEditNote = (event) => {
    const date = new Date(event.date);
    setNoteForm({
      title: event.title,
      description: event.details?.description || '',
      date: event.date.split('T')[0],
      time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    });
    setEditingNote(event.id);
    setShowAddNote(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Eliminare questa nota?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/calendar/private-note/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nota eliminata');
      fetchEvents();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  const days = getDaysInMonth();
  const selectedEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Calendar className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white text-center mb-2" style={{fontFamily: 'Barlow Condensed'}}>
          <TranslatedText text="CALENDARIO" />
        </h2>
        <p className="text-slate-400 text-center text-sm">
          <TranslatedText text="Tutte le attività di Respiro Blu" />
        </p>
      </div>

      {/* Navigazione mese */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="text-center">
          <h3 className="text-xl font-bold text-white">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button
            onClick={goToToday}
            className="text-xs text-cyan-400 hover:text-cyan-300"
          >
            Vai a oggi
          </button>
        </div>
        
        <button
          onClick={goToNextMonth}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Filtri e Aggiungi */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
            showFilters ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-300'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filtri
        </button>
        
        {isAdmin && (
          <button
            onClick={() => {
              setNoteForm({ 
                title: '', 
                description: '', 
                date: selectedDay 
                  ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
                  : '',
                time: '12:00'
              });
              setEditingNote(null);
              setShowAddNote(true);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" />
            Nota privata
          </button>
        )}
      </div>

      {/* Pannello Filtri */}
      {showFilters && (
        <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 mb-4">
          <p className="text-sm text-slate-400 mb-3">Mostra:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(EVENT_TYPES).map(([key, config]) => {
              // Nascondi filtro private se non admin
              if (key === 'private' && !isAdmin) return null;
              
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                    filters[key] 
                      ? `${config.bgColor} ${config.textColor} border border-current` 
                      : 'bg-slate-800 text-slate-500'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Calendario */}
      <div className="bg-slate-900/60 border border-cyan-500/30 rounded-2xl p-4 mb-4">
        {/* Giorni settimana */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map(day => (
            <div key={day} className="text-center text-xs text-slate-500 font-medium py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Griglia giorni */}
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const dayEvents = day ? getEventsForDay(day) : [];
              const hasPrivate = dayEvents.some(e => e.type === 'private');
              
              return (
                <button
                  key={index}
                  onClick={() => day && setSelectedDay(day)}
                  disabled={!day}
                  className={`
                    aspect-square p-1 rounded-lg flex flex-col items-center justify-start
                    ${!day ? 'bg-transparent' : 'hover:bg-slate-800 cursor-pointer'}
                    ${selectedDay === day ? 'bg-cyan-500/20 ring-2 ring-cyan-500' : ''}
                    ${isToday(day) ? 'bg-cyan-500/10' : ''}
                  `}
                >
                  {day && (
                    <>
                      <span className={`text-sm font-medium ${
                        isToday(day) ? 'text-cyan-400' : 'text-white'
                      }`}>
                        {day}
                      </span>
                      
                      {/* Pallini eventi */}
                      <div className="flex flex-wrap justify-center gap-0.5 mt-1">
                        {dayEvents.slice(0, 4).map((event, i) => (
                          <div
                            key={i}
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: event.color }}
                          />
                        ))}
                        {dayEvents.length > 4 && (
                          <span className="text-[8px] text-slate-400">+{dayEvents.length - 4}</span>
                        )}
                      </div>
                      
                      {/* Indicatore privato (solo admin) */}
                      {isAdmin && hasPrivate && (
                        <Lock className="w-2.5 h-2.5 text-slate-500 mt-0.5" />
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap justify-center gap-3 mb-4">
        {Object.entries(EVENT_TYPES).map(([key, config]) => {
          if (key === 'private' && !isAdmin) return null;
          return (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
              <span className="text-xs text-slate-400">{config.label}</span>
            </div>
          );
        })}
      </div>

      {/* Dettaglio giorno selezionato */}
      {selectedDay && (
        <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">
              {selectedDay} {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              onClick={() => setSelectedDay(null)}
              className="p-1 text-slate-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {selectedEvents.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Nessuna attività in questo giorno</p>
          ) : (
            <div className="space-y-3">
              {selectedEvents.map((event) => {
                const config = EVENT_TYPES[event.type];
                const Icon = config.icon;
                
                return (
                  <div
                    key={event.id}
                    className={`${config.bgColor} border border-slate-700/50 rounded-xl p-3`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0`}
                           style={{ backgroundColor: `${config.color}30` }}>
                        <Icon className="w-4 h-4" style={{ color: config.color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${config.textColor}`}>{event.title}</span>
                          {event.is_private && <Lock className="w-3 h-3 text-slate-500" />}
                        </div>
                        {event.subtitle && (
                          <p className="text-sm text-slate-400">{event.subtitle}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {formatTime(event.date)}
                        </div>
                      </div>

                      {/* Azioni per note private */}
                      {event.type === 'private' && isAdmin && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditNote(event)}
                            className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-slate-700 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(event.id)}
                            className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-700 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal Aggiungi/Modifica Nota */}
      {showAddNote && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowAddNote(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-400" />
                {editingNote ? 'Modifica Nota' : 'Nuova Nota Privata'}
              </h3>
              <button onClick={() => { setShowAddNote(false); setEditingNote(null); }} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Titolo *</label>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(e) => setNoteForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Es: Riunione con comune"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Descrizione</label>
                <textarea
                  value={noteForm.description}
                  onChange={(e) => setNoteForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Dettagli opzionali..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Data *</label>
                  <input
                    type="date"
                    value={noteForm.date}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Ora</label>
                  <input
                    type="time"
                    value={noteForm.time}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>

              <button
                onClick={handleAddNote}
                className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {editingNote ? 'Salva Modifiche' : 'Aggiungi Nota'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSection;
